import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import API, { getCompressedFile } from '../context/API';

// Types
export interface UserProfile {
  id: number | string;
  name: string;
  username: string;
  address: string;
  email: string;
  phone: string;
  tax?: number;
  loyaltyEnabled?: boolean;
  loyaltyRedeemRatio?: string;
  loyaltySpendRatio?: string;
  receiveNotifications?: boolean;
  autoRenewSubscription?: boolean;
  role?: string;
  businessName?: string;
  businessAddress?: string;
  contactEmail?: string;
  contactPhone?: string;
  logo?: string | null;
}

export interface MerchantOrManager {
  id: number | string;
  name: string;
  username: string;
  email: string;
  phone: string;
  address: string;
}

export interface UpdateProfilePayload {
  name: string;
  username: string;
  address: string;
  email: string;
  phone: string;
  tax?: number;
  loyaltyEnabled?: boolean;
  loyaltyRedeemRatio?: string;
  loyaltySpendRatio?: string;
  receiveNotifications?: boolean;
  autoRenewSubscription?: boolean;
}

interface SettingsState {
  user: UserProfile | null;
  merchantsAndManagers: MerchantOrManager[];
  filteredMerchantsAndManagers: MerchantOrManager[];
  loading: {
    profile: boolean;
    merchantsAndManagers: boolean;
    update: boolean;
  };
  error: string | null;
  editingSection: string | null;
  showMerchantsAndManagers: boolean;
  settingsSaved: boolean;
}

const initialState: SettingsState = {
  user: null,
  merchantsAndManagers: [],
  filteredMerchantsAndManagers: [],
  loading: {
    profile: false,
    merchantsAndManagers: false,
    update: false,
  },
  error: null,
  editingSection: null,
  showMerchantsAndManagers: false,
  settingsSaved: false,
};

// Async thunks
export const fetchUserProfile = createAsyncThunk<
  UserProfile,
  { role: string },
  { rejectValue: string }
>('settings/fetchUserProfile', async ({ role }, { rejectWithValue }) => {
  try {
    const endpoint = role === 'merchant' ? 'merchant/profile' : 'admin/profile';
    const response = await API.get(endpoint);
    return response.data.data;
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || error.message || 'Failed to fetch user profile'
    );
  }
});

export const updateUserProfile = createAsyncThunk<
  UserProfile,
  { role: string; userId: number | string; data: UpdateProfilePayload; logoFile?: File; removeLogo?: boolean },
  { rejectValue: string }
>('settings/updateUserProfile', async ({ role, userId, data, logoFile, removeLogo }, { rejectWithValue }) => {
  try {
    const endpoint = role === 'merchant' ? `/merchant/${userId}` : `/admin/${userId}`;
    
    // If logo file is provided, use FormData
    if (logoFile) {
      try {
        const compressedFile = await getCompressedFile(logoFile);
        const formData = new FormData();
        
        // Add all profile data fields
        formData.append('name', data.name);
        formData.append('username', data.username);
        formData.append('address', data.address);
        formData.append('email', data.email);
        formData.append('phone', data.phone);
        
        if (data.tax !== undefined) {
          formData.append('tax', String(data.tax));
        }
        if (data.loyaltyEnabled !== undefined) {
          formData.append('loyaltyEnabled', String(data.loyaltyEnabled));
        }
        if (data.loyaltyRedeemRatio !== undefined) {
          formData.append('loyaltyRedeemRatio', data.loyaltyRedeemRatio);
        }
        if (data.loyaltySpendRatio !== undefined) {
          formData.append('loyaltySpendRatio', data.loyaltySpendRatio);
        }
        if (data.receiveNotifications !== undefined) {
          formData.append('receiveNotifications', String(data.receiveNotifications));
        }
        if (data.autoRenewSubscription !== undefined) {
          formData.append('autoRenewSubscription', String(data.autoRenewSubscription));
        }
        
        // Add logo file with exact field name 'logo'
        formData.append('logo', compressedFile);
        
        const response = await API.put(endpoint, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        return response.data.data || { ...data, id: userId };
      } catch (uploadError: any) {
        return rejectWithValue(
          uploadError.response?.data?.message || uploadError.message || 'Failed to update profile with logo'
        );
      }
    } else if (removeLogo) {
      // Logo removal requested - send logo: null in JSON payload
      const dataWithLogoRemoval = { ...data, logo: null };
      const response = await API.put(endpoint, dataWithLogoRemoval);
      return response.data.data || { ...dataWithLogoRemoval, id: userId };
    } else {
      // No logo file and no removal, use regular JSON request
      const response = await API.put(endpoint, data);
      return response.data.data || { ...data, id: userId };
    }
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || error.message || 'Failed to update profile'
    );
  }
});

export const fetchMerchantsAndManagers = createAsyncThunk<
  MerchantOrManager[],
  { role: string },
  { rejectValue: string }
>('settings/fetchMerchantsAndManagers', async ({ role }, { rejectWithValue }) => {
  try {
    const endpoint = role === 'merchant' ? 'manager/all' : 'merchant';
    const response = await API.get(endpoint);
    return response.data.data || [];
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || error.message || 'Failed to fetch merchants and managers'
    );
  }
});

// Slice
const settingsSlice = createSlice({
  name: 'settings',
  initialState,
  reducers: {
    setEditingSection: (state, action: PayloadAction<string | null>) => {
      state.editingSection = action.payload;
      state.settingsSaved = false;
    },
    setShowMerchantsAndManagers: (state, action: PayloadAction<boolean>) => {
      state.showMerchantsAndManagers = action.payload;
    },
    filterMerchantsAndManagers: (state, action: PayloadAction<string>) => {
      const filter = action.payload.toLowerCase();
      if (!filter) {
        state.filteredMerchantsAndManagers = state.merchantsAndManagers;
        return;
      }
      state.filteredMerchantsAndManagers = state.merchantsAndManagers.filter(
        (item) =>
          item.name.toLowerCase().includes(filter) ||
          item.username.toLowerCase().includes(filter) ||
          item.email.toLowerCase().includes(filter)
      );
    },
    setSettingsSaved: (state, action: PayloadAction<boolean>) => {
      state.settingsSaved = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch user profile
    builder
      .addCase(fetchUserProfile.pending, (state) => {
        state.loading.profile = true;
        state.error = null;
      })
      .addCase(fetchUserProfile.fulfilled, (state, action) => {
        state.loading.profile = false;
        state.user = action.payload;
      })
      .addCase(fetchUserProfile.rejected, (state, action) => {
        state.loading.profile = false;
        state.error = action.payload || 'Failed to fetch user profile';
      });

    // Update user profile
    builder
      .addCase(updateUserProfile.pending, (state) => {
        state.loading.update = true;
        state.error = null;
      })
      .addCase(updateUserProfile.fulfilled, (state, action) => {
        state.loading.update = false;
        state.user = { ...state.user, ...action.payload };
        state.editingSection = null;
        state.settingsSaved = true;
      })
      .addCase(updateUserProfile.rejected, (state, action) => {
        state.loading.update = false;
        state.error = action.payload || 'Failed to update profile';
      });

    // Fetch merchants and managers
    builder
      .addCase(fetchMerchantsAndManagers.pending, (state) => {
        state.loading.merchantsAndManagers = true;
        state.error = null;
      })
      .addCase(fetchMerchantsAndManagers.fulfilled, (state, action) => {
        state.loading.merchantsAndManagers = false;
        state.merchantsAndManagers = action.payload;
        state.filteredMerchantsAndManagers = action.payload;
      })
      .addCase(fetchMerchantsAndManagers.rejected, (state, action) => {
        state.loading.merchantsAndManagers = false;
        state.error = action.payload || 'Failed to fetch merchants and managers';
      });
  },
});

export const {
  setEditingSection,
  setShowMerchantsAndManagers,
  filterMerchantsAndManagers,
  setSettingsSaved,
  clearError,
} = settingsSlice.actions;

export default settingsSlice.reducer;

